import { useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { generatePath, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { yupResolver } from '@hookform/resolvers/yup';
import AppRoutes from 'constants/AppRoutes';
import { SORT_DIRECTION_INDICATOR } from 'constants/dataSets';
import {
  ORDER_KEY, ORDER_TYPE, SEARCH, SORT, STATUS,
} from 'constants/formFields';
import { DEFAULT_OFFSET, VALUE_CHAIN_STATUS_DEFAULT } from 'constants/general';
import { ORDERS, VALUE_CHAIN_STATUS } from 'constants/interfaces';
import useDebouncedCallback from 'hooks/useDebouncedCallback';
import i18n from 'i18n';
import { useAppDispatch, useAppSelector } from 'store/hooks/useApp';
import selectProcessingValuesChainMapId from 'store/slices/localStorage/selectors';
import { removeProcessingValuesChainMapId, setProcessingValuesChainMapId } from 'store/slices/localStorage/slice';
import {
  useArchiveValueChainItemMutation,
  useCreateValueChainMapMutation,
  useDeleteValueChainItemMutation,
  useDuplicateValueChainMapMutation,
  useGetValueChainMapListQuery,
  useLazyGetValueChainMapDetailsQuery,
  useRestoreValueChainItemMutation,
  useReviewValueChainItemMutation,
  useUpdateValueChainItemMutation,
} from 'store/slices/valueChainMap/apis/valueChainMapApi';
import { valueChainMapQueryParamsSelector } from 'store/slices/valueChainMap/selectors';
import { resetState, setValueChainMapState } from 'store/slices/valueChainMap/slice';
import { decodeHTML } from 'utils/formatText';
import { valueChainListSchema } from 'utils/validators';
import { getValueChainMapListWithItem } from 'utils/valueChainApi';

import ValueChainMapView from './ValueChainMapView';

import type { ISearchInputHandler } from 'components/TextField/interfaces/ITextField';
import type { TOrderByIdentifier, TValueChainStatus } from 'constants/interfaces';
import type IValueChainMap from 'store/slices/valueChainMap/interfaces/IValueChainMap';
import type { TValueChainMapListSchema } from 'utils/validators';

export default function ValueChainMap() {
  const prevStatusValuesRef = useRef<string[] | undefined>(undefined);
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [valueChainMapCurrentList, setValueChainMapCurrentList] = useState<IValueChainMap[]>([]);
  const valueChainMapQueryParams = useAppSelector(valueChainMapQueryParamsSelector);
  const processingValuesChainMapId = useAppSelector(selectProcessingValuesChainMapId);
  const [openErrorDetailsModal, setOpenErrorDetailsModal] = useState(false);
  const [errorDetails, setErrorDetails] = useState<string | undefined>();

  const {
    control,
    getValues,
    watch,
    setValue,
    formState: { isDirty },
  } = useForm<TValueChainMapListSchema>({
    mode: 'onSubmit',
    reValidateMode: 'onChange',
    resolver: yupResolver(valueChainListSchema),
    defaultValues: {
      [SEARCH]: valueChainMapQueryParams.search,
      [STATUS]: VALUE_CHAIN_STATUS_DEFAULT,
      [SORT]: {
        [ORDER_KEY]: '',
        [ORDER_TYPE]: ORDERS.asc,
      },
    },
  });

  const searchValue = watch(SEARCH);
  const statusValue = watch(STATUS);
  useEffect(() => {
    dispatch(resetState());
  }, [dispatch]);

  const statusValues = statusValue && statusValue?.length > 0
    ? statusValue.filter((v): v is string => v !== undefined) : undefined;
  const statusValuesWithProcessing = processingValuesChainMapId.length > 0 && statusValues
    ? Array.from(new Set([...statusValues, VALUE_CHAIN_STATUS.processing]))
    : statusValues;

  const checkStatusChanged = (): boolean => {
    const statusValuesString = JSON.stringify(statusValues);
    const prevStatusValuesString = JSON.stringify(prevStatusValuesRef.current);
    return statusValuesString !== prevStatusValuesString;
  };

  const hasStatusChanged = checkStatusChanged();

  useEffect(() => {
    prevStatusValuesRef.current = statusValues;
  }, [statusValues]);

  const currentOffset = hasStatusChanged ? 0 : valueChainMapQueryParams.offset;

  const {
    data: valueChainMapList, isLoading,
  } = useGetValueChainMapListQuery({
    search: valueChainMapQueryParams.search || undefined,
    status: statusValuesWithProcessing,
    orderBy: valueChainMapQueryParams.orderBy,
    limit: DEFAULT_OFFSET,
    offset: currentOffset,
  });

  const [updateValueChainItem] = useUpdateValueChainItemMutation();
  const [deleteValueChainItem] = useDeleteValueChainItemMutation();
  const [archiveValueChainItem] = useArchiveValueChainItemMutation();
  const [reviewValueChainItem] = useReviewValueChainItemMutation();
  const [restoreValueChainItem] = useRestoreValueChainItemMutation();
  const [duplicateValueChainMap] = useDuplicateValueChainMapMutation();
  const [createValueChainMap] = useCreateValueChainMapMutation();
  const [getValueChainMapDetails] = useLazyGetValueChainMapDetailsQuery();
  const debouncedSearch = useDebouncedCallback(() => {
    const { search } = getValues();
    dispatch(setValueChainMapState({
      valueChainMapQueryParams: { ...valueChainMapQueryParams, search, offset: 0 },
    }));
  });

  useEffect(() => {
    const items = valueChainMapList?.data.items || [];
    const filteredStatusValues = statusValue?.filter((v): v is string => v !== undefined) || [];

    const visibleItems = filteredStatusValues.length > 0
      ? items.filter((item) => (
        filteredStatusValues.includes(item.status)
        || (item.status === VALUE_CHAIN_STATUS.processing && processingValuesChainMapId.includes(item.id))
      ))
      : items;

    setValueChainMapCurrentList((prevList) => {
      const processingItems = prevList.filter((item) => (
        processingValuesChainMapId.includes(item.id)
        && (filteredStatusValues.length === 0
          || filteredStatusValues.includes(item.status)
          || item.status === VALUE_CHAIN_STATUS.processing)
        && !visibleItems.some((visibleItem) => visibleItem.id === item.id)
      ));

      return [...processingItems, ...visibleItems];
    });
  }, [processingValuesChainMapId, valueChainMapList, statusValue]);

  useEffect(() => {
    if (processingValuesChainMapId.length === 0) {
      return undefined;
    }

    processingValuesChainMapId.forEach((id) => {
      getValueChainMapDetails({ chainId: `${id}` })
        .unwrap()
        .then(({ data }) => {
          setValueChainMapCurrentList((prevList) => getValueChainMapListWithItem(prevList, data));
        })
        .catch(() => {});
    });

    return undefined;
  }, [getValueChainMapDetails, processingValuesChainMapId]);

  // Sync offset in Redux when status changes
  useEffect(() => {
    if (hasStatusChanged && valueChainMapQueryParams.offset !== 0) {
      dispatch(setValueChainMapState({
        valueChainMapQueryParams: { ...valueChainMapQueryParams, offset: 0 },
      }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasStatusChanged, dispatch]);

  const onSearchChange = ({ search, onChange }: ISearchInputHandler) => {
    onChange(search);
    debouncedSearch();
  };

  const onReadMoreClients = () => {
    dispatch(setValueChainMapState({
      valueChainMapQueryParams: {
        ...valueChainMapQueryParams,
        offset: valueChainMapQueryParams.offset + DEFAULT_OFFSET,
      },
    }));
  };

  const debouncedSort = useDebouncedCallback((order: string) => {
    dispatch(setValueChainMapState({
      valueChainMapQueryParams: {
        ...valueChainMapQueryParams, orderBy: order, offset: 0,
      },
    }));
  });

  const onSortChange = (orderByIdentifier: TOrderByIdentifier) => {
    const { sort: { orderKey, orderType } } = getValues();
    const isAsc = orderKey === orderByIdentifier && orderType === ORDERS.asc;
    const order = isAsc ? `${SORT_DIRECTION_INDICATOR}${orderByIdentifier}` : orderByIdentifier;
    setValue(SORT, { [ORDER_KEY]: orderByIdentifier, [ORDER_TYPE]: isAsc ? ORDERS.desc : ORDERS.asc });
    debouncedSort(order);
  };

  useEffect(() => {
    if ((searchValue && !valueChainMapQueryParams?.search)
      || (statusValue && statusValue.length > 0 && !valueChainMapQueryParams?.status)) {
      const querySearchValue = searchValue || valueChainMapQueryParams?.search || undefined;
      dispatch(setValueChainMapState({
        valueChainMapQueryParams: {
          ...valueChainMapQueryParams, search: querySearchValue, offset: 0,
        },
      }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchValue, statusValue, dispatch]);

  useEffect(() => () => {
    dispatch(resetState());
  }, [dispatch]);

  const onChangeStatus = (id: number, status: TValueChainStatus) => {
    updateValueChainItem({ chainId: id, status })
      .unwrap()
      .then(() => toast.success(i18n.t('notifications.statusHasBeenUpdated')));
  };

  const onRenameValueChain = (id: number, name: string) => {
    updateValueChainItem({ chainId: id, name })
      .unwrap()
      .then(() => toast.success(i18n.t('notifications.valueChainMapHasBeenRenamed')));
  };

  const onDeleteValueChain = (id: number) => {
    deleteValueChainItem({ chainId: id })
      .unwrap()
      .then(() => toast.success(i18n.t('notifications.valueChainMapHasBeenDeleted')))
      .catch((error) => {
        setOpenErrorDetailsModal(true);
        const errorMessage = error?.data?.message?.[0][0]?.error || '';
        const translatedMessage = i18n.t(
          'notifications.valueChainMapDeleteError2',
          { error: errorMessage },
        );
        setErrorDetails(decodeHTML(translatedMessage));
        toast.error(i18n.t('notifications.valueChainMapDeleteError'));
      });
  };

  const onArchiveValueChain = (id: number, reason: string) => {
    archiveValueChainItem({ chainId: id, reason })
      .unwrap()
      .then((response) => {
        const currentStatusValues = getValues(STATUS)?.filter((value): value is string => value !== undefined) || [];

        setValueChainMapCurrentList((prevList) => {
          const isArchivedItemVisible = currentStatusValues.length === 0
            || currentStatusValues.includes(response.data.status);

          if (!isArchivedItemVisible) {
            return prevList.filter((item) => item.id !== response.data.id);
          }

          const isItemInList = prevList.some((item) => item.id === response.data.id);

          if (!isItemInList) {
            return [response.data, ...prevList];
          }

          return prevList.map((item) => {
            if (item.id !== response.data.id) {
              return item;
            }

            return {
              ...response.data,
              createdBy: response.data.createdBy || item.createdBy,
            };
          });
        });
        toast.success(i18n.t('notifications.valueChainMapHasBeenArchived'));
      })
      .catch((error) => {
        setOpenErrorDetailsModal(true);
        const errorMessage = error?.data?.message?.[0][0]?.error || '';
        const translatedMessage = i18n.t(
          'notifications.valueChainMapArchiveError2',
          { error: errorMessage },
        );
        setErrorDetails(decodeHTML(translatedMessage));
        toast.error(i18n.t('notifications.valueChainMapArchiveError'));
      });
  };

  const onReviewValueChain = (id: number, reason: string, status: TValueChainStatus) => {
    reviewValueChainItem({ chainId: id, reason, status })
      .unwrap()
      .then(() => {
        if (status === VALUE_CHAIN_STATUS.approved) {
          toast.success(i18n.t('notifications.valueChainMapHasBeenApproved'));
        } else if (status === VALUE_CHAIN_STATUS.requiresRework) {
          toast.success(i18n.t('notifications.valueChainMapHasBeenReWork'));
        }
      });
  };

  const onDeArchiveValueChain = (id: number) => {
    restoreValueChainItem({ chainId: id })
      .unwrap()
      .then(() => toast.success(i18n.t('notifications.valueChainMapHasBeenDeArchived')));
  };

  const onEditValueChain = (valueChainMapId: number) => {
    navigate(generatePath(AppRoutes.companyMap, { valueChainMapId: `${valueChainMapId}` }));
  };

  const onDublicateValueChain = (id: number, name: string) => {
    duplicateValueChainMap({ valueChainMapId: id, name })
      .unwrap()
      .then((data) => {
        dispatch(setProcessingValuesChainMapId(data.data.id));
        setValueChainMapCurrentList((prevList) => getValueChainMapListWithItem(prevList, data.data));
        toast.success(i18n.t('notifications.valueChainMapHasBeenDublicated'));
      }).catch((error) => {
        dispatch(removeProcessingValuesChainMapId(id));
        toast.error(i18n.t('notifications.valueChainMapDuplicatedError', { error: error.data.error }));
      });
  };

  const onCreateNewValueChain = (name: string) => {
    createValueChainMap({ name })
      .unwrap()
      .then((data) => {
        dispatch(setProcessingValuesChainMapId(data.data.id));
        setValueChainMapCurrentList((prevList) => getValueChainMapListWithItem(prevList, data.data));
        toast.success(i18n.t('notifications.valueChainMapHasBeenCreated'));
      }).catch((error) => {
        toast.error(i18n.t('notifications.valueChainMapCreateError', { error: error.data.error }));
      });
  };

  const handleCloseErrorDetailsModal = () => {
    setOpenErrorDetailsModal(false);
    setErrorDetails(undefined);
  };

  return (
    <ValueChainMapView
      control={control}
      valueChainList={valueChainMapCurrentList}
      onReadMoreClients={onReadMoreClients}
      hasNext={!!valueChainMapList?.data.meta.hasNext}
      onSearch={onSearchChange}
      onSortChange={onSortChange}
      isWithSearch={isDirty}
      isPageLoading={isLoading}
      onChangeStatus={onChangeStatus}
      onRenameValueChain={onRenameValueChain}
      onDeleteValueChain={onDeleteValueChain}
      onEditValueChain={onEditValueChain}
      onArchiveValueChain={onArchiveValueChain}
      onDublicateValueChain={onDublicateValueChain}
      onDeArchiveValueChain={onDeArchiveValueChain}
      onCreateNewValueChain={onCreateNewValueChain}
      onReviewValueChain={onReviewValueChain}
      openErrorDetailsModal={openErrorDetailsModal}
      errorDetails={errorDetails}
      handleCloseErrorDetailsModal={handleCloseErrorDetailsModal}
    />
  );
}
